import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { InvoiceDetailComponent } from '../invoice-detail-component/invoice-detail-component';
import { LoadDetails, LoadDetailsResponse, DeliveryService } from '../../services/delivery.service';
import { MessageDialogService } from '../../services/message-dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface InvoiceReportItem {
  customerId: number;
  customerName: string | null;
  salesOrder: string | null;
  invoiceNumber: string | null;
  invoiceId: number;                 // ⬅️ required to fetch details
  invoicedAmount: number;
  balanceExcludingThisInvoice: number;
  balanceExcludingThisLoad: number;
  totalOutstandingForCustomer: number;
  delivered?: boolean;               // optional, if provided from backend
  paymentState?: 'paid' | 'reversed' | 'not_paid' | 'in_payment' | 'partial'; // payment state
}

@Component({
  selector: 'app-invoices-report-table',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    InvoiceDetailComponent
  ],
  templateUrl: './invoices-report-table-component.html',
  styleUrls: ['./invoices-report-table-component.scss']
})
export class InvoicesReportTableComponent implements OnChanges {
  private readonly detailsHistoryStateKey = 'a1-route-sales-invoice-details';
  private readonly returnHistoryStateKey = 'a1-route-sales-invoice-return';
  @Input() load?: LoadDetails;
  @Output() closedInvoiceDetails = new EventEmitter();

  displayedColumns: string[] = [
    'sequence',
    'customerName',
    'salesOrder',
    'invoiceNumber',
    'invoicedAmount',
    'balanceExcludingThisLoad',
    'totalOutstandingForCustomer',
    'actions'
  ];
  footerColumns = this.displayedColumns;

  dataSource = new MatTableDataSource<InvoiceReportItem>([]);
  totals = {
    invoicedAmount: 0,
    balanceExcludingThisLoad: 0,
    totalOutstandingForCustomer: 0
  };

  /** ⬇️ selection state for the detail panel */
  selectedInvoiceId: number | null = null;
  private _showDetails = false;
  processingDeliverId: number | null = null;

  get showDetails(): boolean {
    return this._showDetails;
  }

  set showDetails(value: boolean) {
    this._showDetails = value;
    // Reload dataSource when showDetails changes to false
    if (!value) {
      this.reloadDataSource();
    }
  }

  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  constructor(
    private readonly deliveryService: DeliveryService,
    private readonly messageDialog: MessageDialogService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('load' in changes) {
      this.dataSource = new MatTableDataSource<InvoiceReportItem>(this.getInvoiceReport());
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
      this.computeTotals();

      this.dataSource.filterPredicate = (row, filter) =>
        JSON.stringify(row).toLowerCase().includes(filter.trim().toLowerCase());
    }
  }

  getInvoiceReport(): InvoiceReportItem[] | undefined {
    return this.load?.invoicesReport || []
  }

  applyFilter(value: string) {
    this.dataSource.filter = (value || '').trim().toLowerCase();
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  private computeTotals() {
    const sum = (getter: (r: InvoiceReportItem) => number) =>
      (this.getInvoiceReport() || []).reduce((a, r) => a + (Number(getter(r)) || 0), 0);

    const sumIndividual = (getter: (r: InvoiceReportItem) => number) =>
      (this.getInvoiceReport() || []).filter((value, index, self) =>
        self.findIndex((item) => item.customerId === value.customerId) === index
      ).reduce((a, r) => a + (Number(getter(r)) || 0), 0);

    this.totals.invoicedAmount = sum(r => r.invoicedAmount);
    this.totals.totalOutstandingForCustomer = sumIndividual(r => r.totalOutstandingForCustomer);    
    this.totals.balanceExcludingThisLoad = this.totals.totalOutstandingForCustomer - this.totals.invoicedAmount;
  }

  private reloadDataSource() {
    const loadId = this.load?.id;
    if (!loadId) {
      // Fallback to local data if no load ID
      this.dataSource = new MatTableDataSource<InvoiceReportItem>(this.getInvoiceReport());
      this.dataSource.sort = this.sort;
      this.dataSource.paginator = this.paginator;
      this.computeTotals();
      this.dataSource.filterPredicate = (row, filter) =>
        JSON.stringify(row).toLowerCase().includes(filter.trim().toLowerCase());
      return;
    }

    // Fetch fresh data from server
    this.deliveryService.getLoadDetails(loadId).subscribe({
      next: (res) => {
        if (res.status === 'success' && res.load) {
          // Update the load data with fresh data from server
          this.load = res.load;
          // Update data source with fresh invoice report data
          this.dataSource = new MatTableDataSource<InvoiceReportItem>(this.getInvoiceReport());
          this.dataSource.sort = this.sort;
          this.dataSource.paginator = this.paginator;
          this.computeTotals();
          this.dataSource.filterPredicate = (row, filter) =>
            JSON.stringify(row).toLowerCase().includes(filter.trim().toLowerCase());
        }
      },
      error: (err) => {
        console.error('Failed to reload load details:', err);
        // Fallback to local data on error
        this.dataSource = new MatTableDataSource<InvoiceReportItem>(this.getInvoiceReport());
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
        this.computeTotals();
        this.dataSource.filterPredicate = (row, filter) =>
          JSON.stringify(row).toLowerCase().includes(filter.trim().toLowerCase());
      }
    });
  }

  /** ⬇️ double-click handler */
  openInvoiceDetails(row: InvoiceReportItem) {
    if (!row?.invoiceId) return;
    if (!this.canOpenInvoiceDetails()) return; // block when not in transit
    this.selectedInvoiceId = row.invoiceId;
    this.showDetails = true;
    this.pushDetailsHistoryState();
  }

  /** whether invoice details can be opened based on load state */
  canOpenInvoiceDetails(): boolean {
    return this.load?.state === 'in_transit';
  }

  /** handle Start Delivery action */
  startDelivery() {
    const loadId = this.load?.id;
    if (!loadId) return;
    const loadName = this.load?.name || `#${loadId}`;
    this.messageDialog.confirm(`Start delivery for load ${loadName}?`, 'Confirm Start Delivery')
      .subscribe((ok: boolean) => {
        if (!ok) return;
        this.deliveryService.setLoadState(loadId, 'in_transit').subscribe({
          next: (res) => {
            if (res.status === 'success') {
              if (this.load) this.load.state = res.load?.state || 'in_transit';
            }
          },
          error: (_err) => {
            // optionally surface error to user
          }
        });
      });
  }

  /** mark a single invoice as delivered */
  markDelivered(row: InvoiceReportItem) {
    const loadId = this.load?.id;
    if (!loadId || !row?.invoiceId) return;
    if (!this.canOpenInvoiceDetails()) return;

    // Prompt user for confirmation
    this.messageDialog.confirm(
      `Are you sure you want to mark invoice ${row.invoiceNumber || row.invoiceId} as delivered?`,
      'Confirm Delivery'
    ).subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      
      this.processingDeliverId = row.invoiceId;
      this.deliveryService.markInvoiceDelivered({
        load_id: loadId,
        invoice_id: row.invoiceId,
        delivered: true
      }).subscribe({
        next: (res) => {
          if (res.status === 'success') {
            // reflect delivered state locally if present in table data
            const data = this.dataSource.data.map(d => 
              d.invoiceId === row.invoiceId ? { ...d, delivered: true } : d
            );
            this.dataSource.data = data;
          }
        },
        error: (_err) => {
          // optionally surface error to user
        }
      });
    });
  }

  exportCsv() {
    const headers = [
      'Customer',
      'Sales Order',
      'Invoice',
      'Invoiced Amount',
      'Balance Excluding This Invoice',
      'Total Outstanding'
    ];

    const rows = (this.getInvoiceReport() || []).map(r => [
      r.customerName ?? '',
      r.salesOrder ?? '',
      r.invoiceNumber ?? '',
      r.invoicedAmount,
      r.balanceExcludingThisLoad,
      r.totalOutstandingForCustomer
    ]);

    const all = [
      headers,
      ...rows,
      ['TOTAL', '', '', this.totals.invoicedAmount, this.totals.balanceExcludingThisLoad, this.totals.totalOutstandingForCustomer]
    ];
    const csv = all.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoices_report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** ⬇️ close handlers (button, child event, or browser Back) */
  closeDetails(syncBrowserHistory: boolean = true) {
    if (this.showDetails) {
      if (syncBrowserHistory && this.isCurrentDetailsHistoryState()) {
        window.history.back();
        return;
      }

      this.showDetails = false;
      this.selectedInvoiceId = null;
    }
    else
      this.closedInvoiceDetails.emit();
  }

  @HostListener('window:popstate', ['$event'])
  onBrowserBack(event: PopStateEvent): void {
    if (!this.showDetails) return;

    // Back from Return screen lands on Invoice Details state.
    // Let InvoiceDetailComponent close only the Return screen.
    if (event.state?.screen === this.detailsHistoryStateKey || event.state?.screen === this.returnHistoryStateKey) return;

    this.closeDetails(false);
  }

  private pushDetailsHistoryState(): void {
    if (this.isCurrentDetailsHistoryState()) return;
    window.history.pushState({ screen: this.detailsHistoryStateKey }, '', window.location.href);
  }

  private isCurrentDetailsHistoryState(): boolean {
    return window.history.state?.screen === this.detailsHistoryStateKey;
  }

  onChildCollectPayment(_invoice: any) {
    // optional: open your collect dialog here
  }

  onChildLoaded(_invoice: any) {
    // optional: react to load complete
  }

  isCollected(invoice: InvoiceReportItem): boolean {
    return invoice.paymentState === 'paid' || invoice.paymentState === 'reversed';
  }
}
